import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Star, Users, Search, Filter, Zap, BookOpen, GraduationCap, WifiOff, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { useReducedMotion } from 'framer-motion';
import { staggerContainer, fadeUp, maybe } from '../lib/motionVariants';
import axios from 'axios';
import WhatsAppContactButton from '../components/WhatsAppContactButton';
import CourseCover from '../components/course/CourseCover';
import { getStudentCount } from '../lib/studentCount';
import { hasRating } from '../lib/courseRating';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Kart izgarasi 2/3/4 sutunlu: 24, hepsine tam bolunur.
export const PAGE_SIZE = 24;

export default function CourseCatalog() {
  const { t, getField, lang } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [courses, setCourses] = useState([]);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState(false);
  // Son sayfa PAGE_SIZE'dan kisa geldiyse cekilecek bir sey kalmamistir.
  const [exhausted, setExhausted] = useState(false);
  // Sunucudan ham cekilen kayit sayisi (dedup'tan bagimsiz) = sonraki skip.
  const fetchedRef = useRef(0);
  // Filtre degisince ucustaki eski istekleri gecersiz kilmak icin istek kimligi.
  const requestIdRef = useRef(0);

  const reduced = useReducedMotion();

  const category = searchParams.get('category') || '';
  const sort = searchParams.get('sort') || 'newest';
  const level = searchParams.get('level') || '';
  // Modul 3.7.3: cabin_crew kategorisinde level_required filtresi (client-side)
  const [levelRequiredFilter, setLevelRequiredFilter] = useState('all');

  const buildParams = useCallback((skip) => {
    const params = new URLSearchParams();
    if (category) params.set('category', category);
    if (searchTerm) params.set('search', searchTerm);
    if (sort) params.set('sort', sort);
    if (level) params.set('level', level);
    params.set('limit', String(PAGE_SIZE));
    params.set('skip', String(skip));
    return params;
  }, [category, searchTerm, sort, level]);

  // Ilk sayfa: kategori/arama/seviye/siralama her degistiginde liste sifirlanir (skip=0).
  const fetchData = useCallback(async () => {
    const reqId = ++requestIdRef.current;
    setLoading(true);
    setError(false);
    setLoadMoreError(false);
    setLoadingMore(false);
    try {
      const [coursesRes, catsRes] = await Promise.all([
        axios.get(`${API}/courses?${buildParams(0).toString()}`),
        axios.get(`${API}/courses/categories`)
      ]);
      if (reqId !== requestIdRef.current) return;
      const page = coursesRes.data.courses || [];
      fetchedRef.current = page.length;
      setCourses(page);
      setTotal(coursesRes.data.total ?? page.length);
      setExhausted(page.length < PAGE_SIZE);
      setCategories(catsRes.data.categories);
    } catch (err) {
      if (reqId !== requestIdRef.current) return;
      console.error('Error:', err);
      setError(true);
    } finally {
      if (reqId === requestIdRef.current) setLoading(false);
    }
  }, [buildParams, refetchTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sonraki sayfa: mevcut listeye eklenir (course_id ile tekillestirilir).
  const loadMore = async () => {
    if (loadingMore || loading) return;
    const reqId = requestIdRef.current;
    setLoadingMore(true);
    setLoadMoreError(false);
    try {
      const res = await axios.get(`${API}/courses?${buildParams(fetchedRef.current).toString()}`);
      if (reqId !== requestIdRef.current) return;
      const page = res.data.courses || [];
      fetchedRef.current += page.length;
      setCourses(prev => {
        const seen = new Set(prev.map(c => c.course_id));
        return [...prev, ...page.filter(c => !seen.has(c.course_id))];
      });
      if (typeof res.data.total === 'number') setTotal(res.data.total);
      setExhausted(page.length < PAGE_SIZE);
    } catch (err) {
      if (reqId !== requestIdRef.current) return;
      console.error('Error:', err);
      setLoadMoreError(true);
    } finally {
      if (reqId === requestIdRef.current) setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = (e) => {
    e.preventDefault();
    const newParams = new URLSearchParams(searchParams);
    if (searchTerm) newParams.set('search', searchTerm);
    else newParams.delete('search');
    setSearchParams(newParams);
  };

  const updateFilter = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) newParams.set(key, value);
    else newParams.delete(key);
    setSearchParams(newParams);
  };

  // Modul 3.7.3: cabin_crew seciliyken level_required client-side filtre
  const visibleCourses = courses.filter(c => {
    if (category === 'cabin_crew' && levelRequiredFilter !== 'all' && c.level_required !== levelRequiredFilter) return false;
    return true;
  });
  const hasMore = !exhausted && courses.length < total;

  const hasActiveFilters = !!(category || level || searchParams.get('search'));

  const clearAllFilters = () => {
    setSearchTerm('');
    setSearchParams(new URLSearchParams());
    setLevelRequiredFilter('all');
  };

  useEffect(() => {
    document.body.classList.add('duo-page');
    return () => document.body.classList.remove('duo-page');
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="course-catalog-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Page Header */}
        <motion.div
          className="mb-10 flex items-end justify-between gap-4 flex-wrap"
          variants={maybe(reduced, fadeUp)}
          initial="hidden"
          animate="visible"
        >
          <div>
            <h1 className="font-duo-display text-3xl sm:text-4xl text-[var(--duo-text)] mb-1">
              {t('catalog.heading')}
            </h1>
            <p className="text-[var(--duo-text-soft)] font-semibold text-base">
              {t('catalog.subheading')}
            </p>
          </div>
          {!loading && !error && (
            <span className="text-sm text-[var(--duo-text-soft)] font-bold shrink-0">
              {t('catalog.resultCount').replace('{{count}}', total)}
            </span>
          )}
        </motion.div>

        {/* Search Bar */}
        <div className="mb-8">
          <form onSubmit={handleSearch} className="flex gap-3">
            <div className="flex-1 input-duo-wrap">
              <Search className="input-duo-icon" size={20} strokeWidth={2.5} />
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder={t('catalog.search.placeholder')}
                className="input-duo w-full"
                data-testid="course-search-input"
              />
            </div>
            <button type="submit" className="btn-duo-primary px-5 py-3" data-testid="course-search-btn">
              <Search size={20} strokeWidth={2.5} />
            </button>
          </form>
        </div>

        {/* Category filter chips — yatay scroll strip */}
        <div className="flex gap-3 mb-6 overflow-x-auto pb-1 [&::-webkit-scrollbar]:hidden">
          <button
            onClick={() => updateFilter('category', '')}
            aria-pressed={!category}
            className={`px-4 py-2 rounded-xl font-bold text-sm border-2 transition-all shrink-0 ${!category ? 'bg-[var(--duo-green)] text-white border-transparent shadow-[0_2px_0_0_var(--duo-green-dark)]' : 'bg-white border-[var(--duo-stroke)] text-[var(--duo-text-soft)] hover:border-[var(--duo-green)]'}`}
            data-testid="filter-all"
          >
            {t('catalog.filterAll')}
          </button>
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => updateFilter('category', cat.id)}
              aria-pressed={category === cat.id}
              className={`px-4 py-2 rounded-xl font-bold text-sm border-2 transition-all shrink-0 ${category === cat.id ? 'bg-[var(--duo-green)] text-white border-transparent shadow-[0_2px_0_0_var(--duo-green-dark)]' : 'bg-white border-[var(--duo-stroke)] text-[var(--duo-text-soft)] hover:border-[var(--duo-green)]'}`}
              data-testid={`filter-${cat.id}`}
            >
              {getField(cat, 'name')}
            </button>
          ))}
        </div>

        {/* Sort & Level */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <div className="flex items-center gap-2">
            <Filter size={16} strokeWidth={2.5} className="text-[var(--duo-text-soft)]" />
            <select
              value={sort}
              onChange={e => updateFilter('sort', e.target.value)}
              className="px-3 py-2 rounded-xl border-2 border-[var(--duo-stroke)] bg-[#F7F7F7] font-semibold text-sm focus:outline-none focus:border-[var(--duo-blue)]"
              data-testid="sort-select"
            >
              <option value="newest">{t('catalog.sort.newest')}</option>
              <option value="popular">{t('catalog.sort.popular')}</option>
              <option value="rating">{t('catalog.sort.rating')}</option>
            </select>
          </div>
          <div className="flex gap-2">
            {['beginner', 'intermediate', 'advanced'].map(l => (
              <button
                key={l}
                onClick={() => updateFilter('level', level === l ? '' : l)}
                aria-pressed={level === l}
                className={`px-3 py-1.5 rounded-lg font-bold text-xs border-2 transition-all ${level === l ? 'bg-[var(--duo-green)] text-white border-transparent shadow-[0_2px_0_0_var(--duo-green-dark)]' : 'bg-white border-[var(--duo-stroke)] text-[var(--duo-text-soft)]'}`}
                data-testid={`level-${l}`}
              >
                {t(l)}
              </button>
            ))}
          </div>
        </div>

        {/* Modül 3.7.3: Kabin memurluğu seviye filtresi (sadece cabin_crew seçiliyken) */}
        {category === 'cabin_crew' && (
          <div className="flex gap-2 flex-wrap mb-6" data-testid="cabin-crew-level-filter">
            {['all', 'beginner', 'intermediate', 'advanced'].map(lvl => (
              <button
                key={lvl}
                onClick={() => setLevelRequiredFilter(lvl)}
                aria-pressed={levelRequiredFilter === lvl}
                className={`px-3 py-1 rounded-xl text-xs font-bold border-2 transition-all ${
                  levelRequiredFilter === lvl
                    ? 'bg-[var(--duo-blue)] text-white border-transparent'
                    : 'bg-white text-[var(--duo-text-soft)] border-[var(--duo-stroke)]'
                }`}
                data-testid={`level-required-${lvl}`}
              >
                {lvl === 'all' ? t('catalog.levelFilter.all') : t(lvl)}
              </button>
            ))}
          </div>
        )}

        {/* Course Grid */}
        {loading ? (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
            aria-label={t('catalog.loading.ariaLabel')}
            aria-busy="true"
          >
            {[...Array(8)].map((_, i) => (
              <div key={i} className="card-duo overflow-hidden animate-pulse">
                <div className="h-36 rounded-tl-[18px] rounded-tr-[18px] bg-gradient-to-r from-[var(--duo-stroke)] to-[var(--border-subtle)] animate-pulse" />
                <div className="p-5 space-y-3">
                  <div className="h-4 bg-[var(--duo-stroke)] rounded w-3/4" />
                  <div className="h-3 bg-[var(--duo-stroke)] rounded w-full" />
                  <div className="h-3 bg-[var(--duo-stroke)] rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-20">
            <WifiOff size={48} strokeWidth={2} className="mx-auto text-[var(--brand-danger)] mb-4" />
            <p className="font-duo-display text-xl font-bold text-[var(--duo-text-soft)] mb-2">
              {t('catalog.error.title')}
            </p>
            <p className="text-sm text-[var(--duo-text-soft)] mb-6">
              {t('catalog.error.sub')}
            </p>
            <button
              onClick={() => setRefetchTrigger(n => n + 1)}
              className="btn-duo-blue px-6 py-3"
            >
              {t('common.retry')}
            </button>
          </div>
        ) : visibleCourses.length === 0 ? (
          <div className="text-center py-20">
            <BookOpen size={52} strokeWidth={2} className="mx-auto text-[var(--duo-text-soft)] mb-4" />
            <p className="font-duo-display text-xl font-bold text-[var(--duo-text-soft)] mb-2">
              {t('catalog.empty.title')}
            </p>
            <p className="text-sm text-[var(--duo-text-soft)] mb-6">
              {t('catalog.empty.sub')}
            </p>
            {hasActiveFilters ? (
              <div className="flex flex-wrap items-center justify-center gap-3">
                <button
                  onClick={clearAllFilters}
                  className="btn-duo-secondary px-6 py-3"
                >
                  {t('catalog.empty.clearFilters')}
                </button>
                <WhatsAppContactButton variant="secondary" testId="catalog-empty-whatsapp-btn" />
              </div>
            ) : (
              <WhatsAppContactButton variant="primary" testId="catalog-empty-whatsapp-btn" />
            )}
          </div>
        ) : (
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
            variants={maybe(reduced, staggerContainer(0.07))}
            initial="hidden"
            animate="visible"
          >
            {visibleCourses
              .map((course) => {
                return (
                  <motion.div
                    key={course.course_id}
                    variants={maybe(reduced, fadeUp)}
                    whileHover={reduced ? undefined : { y: -5, scale: 1.02, transition: { duration: 0.2, ease: [0.22, 0.61, 0.36, 1] } }}
                    whileTap={reduced ? undefined : { scale: 0.985 }}
                    className="h-full min-h-[340px]"
                  >
                    <Link to={`/courses/${course.course_id}`} data-testid={`catalog-course-${courses.indexOf(course)}`} className="card-duo h-full flex flex-col overflow-hidden">
                      {/* Card top zone — kapak gorseli; yoksa/yuklenmezse kategori gradient + ikon */}
                      <CourseCover
                        thumbnail={course.thumbnail}
                        category={course.category}
                        className="h-36 shrink-0"
                        style={{ borderBottom: '2px solid var(--duo-stroke)' }}
                      >
                        {course.is_interactive && (
                          <span className="absolute top-3 left-3 text-[11px] font-bold px-2.5 py-1 rounded-full bg-white/90 backdrop-blur-sm border border-white/40 flex items-center gap-1">
                            <Zap size={11} strokeWidth={2.5} className="text-[var(--brand-success)]" /> {t('interactive_course')}
                          </span>
                        )}
                        {course.level_required && course.category === 'cabin_crew' && (
                          <span
                            className="absolute top-3 right-3 text-xs font-bold px-2.5 py-1 rounded-xl bg-white/90 backdrop-blur-sm border border-white/40"
                            data-testid={`level-required-badge-${course.course_id}`}
                          >
                            {course.level_required.toUpperCase()}
                          </span>
                        )}
                      </CourseCover>

                      <div className="p-5 flex flex-col flex-1">
                        <h3
                          className="font-duo-display text-base mb-1 line-clamp-2 overflow-hidden leading-snug min-h-[2.75rem] text-[var(--duo-text)]"
                          title={getField(course, 'title')}
                        >
                          {getField(course, 'title')}
                        </h3>
                        <p className="text-sm text-[var(--text-secondary)] mb-3 font-semibold line-clamp-1 flex items-center gap-1.5">
                          <GraduationCap size={13} strokeWidth={2.5} className="text-[var(--duo-text-soft)] shrink-0" aria-hidden="true" />
                          {course.instructor_name || '—'}
                        </p>
                        <div className="flex items-center gap-2 mb-3">
                          {hasRating(course) && (
                            <>
                              <Star size={14} strokeWidth={2.5} className="text-[var(--duo-orange)]" fill="var(--duo-orange)" />
                              <span className="text-sm font-bold">{course.rating_avg ?? '—'}</span>
                              <span className="text-xs text-[var(--duo-text-soft)]">({course.rating_count})</span>
                            </>
                          )}
                          <span className="text-xs text-[var(--duo-text-soft)] ml-auto flex items-center gap-1">
                            <Users size={12} strokeWidth={2.5} /> {getStudentCount(course)}
                          </span>
                        </div>
                        <div className="mt-auto pt-1 flex items-center gap-1 text-sm font-bold text-[var(--brand-primary)]">
                          <BookOpen size={14} strokeWidth={2.5} aria-hidden="true" />
                          <span>{(course.lessons_count || 0)} {lang === 'tr' ? 'ders' : 'lessons'}</span>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
          </motion.div>
        )}

        {/* Sayfalama: "Daha fazla goster" — tum kurslar yuklenince kaybolur */}
        {!loading && !error && total > 0 && (
          <div className="mt-8 flex flex-col items-center gap-3" data-testid="catalog-pagination">
            <span className="text-sm text-[var(--duo-text-soft)] font-bold" data-testid="catalog-shown-count">
              {t('catalog.shownCount')
                .replace('{{shown}}', visibleCourses.length)
                .replace('{{total}}', total)}
            </span>
            {loadMoreError && (
              <p className="text-sm font-semibold text-[var(--brand-danger)]" role="alert" data-testid="catalog-load-more-error">
                {t('catalog.loadMore.error')}
              </p>
            )}
            {hasMore && (
              <button
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                className="btn-duo-secondary px-6 py-3 flex items-center gap-2 disabled:opacity-60"
                data-testid="catalog-load-more-btn"
              >
                {loadingMore && <Loader2 size={18} strokeWidth={2.5} className="animate-spin" aria-hidden="true" />}
                {loadingMore ? t('catalog.loadMore.loading') : t('catalog.loadMore')}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
